/**
 * The non-UI truck steering session backend.
 *
 * Mirrors the moq-boy `Game` class: a video pipeline pulled from `@moq/watch`,
 * a render-only `control` track we publish, and a `status` track we subscribe to
 * for round-trip confirmation. The UI layer reads signals from this class to
 * render video, the control panel, and telemetry.
 *
 * @module
 */
import * as Json from "@moq/json";
import * as Moq from "@moq/net";
import * as Watch from "@moq/watch";
import { REST } from "./input.ts";
import type { DriveState, Gear, TruckStatus } from "./schemas.ts";
import { TruckStatusSchema } from "./schemas.ts";

/** Configuration for creating a Truck session. */
export interface TruckConfig {
	/** Broadcast id as announced by the relay, e.g. "truck1/front.hang". */
	broadcastId: Moq.Path.Valid;
	/** MoQ connection to the relay. */
	connection: Moq.Connection.Reload;
	/** Shared signal tracking which truck is currently being driven. */
	driving: Moq.Signals.Signal<string | undefined>;
}

/** A control command with captured timestamps, published via the command signal. */
interface Command {
	cmd: Record<string, unknown>;
	sequence: number;
	timestamps: { label: string; ts: number }[];
}

const CONTROL_HEARTBEAT_MS = 250;
const MAX_PENDING_ACKS = 64;

/**
 * A single truck streaming + steering session (the non-UI backend).
 */
export class Truck {
	/** Stable id with the catalog suffix stripped, e.g. "truck1/front". */
	readonly id: string;
	readonly broadcastId: Moq.Path.Valid;
	readonly #signals = new Moq.Signals.Effect();
	readonly #driving: Moq.Signals.Signal<string | undefined>;

	// Where this truck's drivers publish their control broadcasts.
	readonly #driverPrefix: string;

	// Reactive state exposed to the UI.
	readonly active = new Moq.Signals.Signal(false);
	readonly latency = new Moq.Signals.Signal<Watch.Latency>("real-time");
	readonly status = new Moq.Signals.Signal<TruckStatus | undefined>(undefined);
	readonly driverId = new Moq.Signals.Signal<string | undefined>(undefined);
	readonly estopped = new Moq.Signals.Signal(false);
	readonly roundTripMs = new Moq.Signals.Signal<number | undefined>(undefined);

	// The driver's locally-requested drive state (what we are sending).
	readonly drive = new Moq.Signals.Signal<DriveState>({ ...REST });

	// Watch API objects, exposed so the UI can attach a canvas.
	readonly broadcast: Watch.Broadcast;
	readonly sync: Watch.Sync;
	readonly videoSource: Watch.Video.Source;
	readonly videoDecoder: Watch.Video.Decoder;
	readonly videoRenderer: Watch.Video.Renderer;

	// Internal control publishing state.
	#command = new Moq.Signals.Signal<Command | undefined>(undefined);
	#sequence = 0;
	#sentAt = new Map<number, number>();

	// The canvas is created by the UI layer after construction, so the renderer
	// takes it as a signal input rather than a property set afterwards.
	readonly #canvas = new Moq.Signals.Signal<HTMLCanvasElement | undefined>(undefined);

	constructor(config: TruckConfig) {
		const { broadcastId, connection, driving } = config;
		this.broadcastId = broadcastId;
		this.id = stripSuffix(broadcastId, ".hang");
		this.#driving = driving;
		this.#driverPrefix = `${this.id}/driver`;

		// Active when this truck is the one being driven.
		this.#signals.run(this.#runActive.bind(this));
		this.#signals.run(this.#runControlLease.bind(this));
		this.#signals.interval(() => this.#heartbeat(), CONTROL_HEARTBEAT_MS);

		// Video pipeline.
		this.broadcast = new Watch.Broadcast({
			connection: connection.established,
			name: broadcastId,
			enabled: true,
		});
		this.#signals.cleanup(() => this.broadcast.close());

		// The source produces the jitter estimate Sync reads, so it is built first
		// to avoid a construction cycle.
		this.videoSource = new Watch.Video.Source({
			broadcast: this.broadcast,
			supported: Watch.Video.Decoder.supported,
		});
		this.#signals.cleanup(() => this.videoSource.close());

		this.sync = new Watch.Sync({
			latency: this.latency,
			connection: connection.established,
			video: this.videoSource.out.jitter,
		});
		this.#signals.cleanup(() => this.sync.close());

		// Only the selected truck pulls video. The status track stays subscribed for
		// every tile, but one visitor must never wake both software encoders on the
		// shared-vCPU demo VM. Passing the signal also fixes the decoder's default of
		// disabled, which would otherwise leave every tile permanently dark.
		this.videoDecoder = new Watch.Video.Decoder(this.videoSource, this.sync, { enabled: this.active });
		this.#signals.cleanup(() => this.videoDecoder.close());

		this.videoRenderer = new Watch.Video.Renderer(this.videoDecoder, { canvas: this.#canvas });
		this.#signals.cleanup(() => this.videoRenderer.close());

		// Subscribe to the truck's status (telemetry) track.
		this.#signals.run(this.#runStatus.bind(this));

		// Publish control while this truck is being driven.
		this.#signals.run(this.#runControl.bind(this, connection));
	}

	/** Attach the canvas the UI created so the renderer can draw into it. */
	setCanvas(canvas: HTMLCanvasElement | undefined) {
		this.#canvas.set(canvas);
	}

	/** Merge a partial drive update and publish it. */
	setDrive(patch: Partial<DriveState>) {
		if (this.estopped.peek()) return;
		const next = { ...this.drive.peek(), ...patch };
		this.drive.set(next);
		this.#send({ type: "drive", ...next });
	}

	/** Request an emergency stop. */
	estop() {
		this.drive.set({ ...REST });
		this.estopped.set(true);
		this.#send({ type: "estop" });
	}

	/**
	 * Latch e-stop on this truck even without holding the driving lease. Anyone
	 * may hit the big red button; the truck latches a bystander e-stop without
	 * granting them the lease. A pure viewer publishes no control track, so claim
	 * this truck locally first - that publishes a driver broadcast the estop can
	 * ride out on (the 250 ms heartbeat guarantees delivery once it is live).
	 */
	emergencyStop() {
		if (!this.active.peek()) this.#driving.set(this.id);
		this.estop();
	}

	/** Explicitly clear the render-only e-stop latch into the full-brake state. */
	resetEstop() {
		// Gate on the truck's latch, not just our optimistic local copy: a
		// rejected reset (someone else held the lease) clears the local signal
		// without a correcting status echo, and requiring it here would then
		// deadlock this driver out of ever resetting again.
		const latched = this.estopped.peek() || this.status.peek()?.estopped;
		if (!this.active.peek() || !latched) return;
		this.drive.set({ ...REST });
		this.estopped.set(false);
		this.#send({ type: "reset_estop" });
	}

	#send(cmd: Record<string, unknown>) {
		this.#sequence = (this.#sequence % Number.MAX_SAFE_INTEGER) + 1;
		this.#sentAt.set(this.#sequence, performance.now());
		while (this.#sentAt.size > MAX_PENDING_ACKS) {
			const oldest = this.#sentAt.keys().next().value;
			if (oldest == null) break;
			this.#sentAt.delete(oldest);
		}
		this.#command.set({ cmd, sequence: this.#sequence, timestamps: this.#timestamps() }, true);
	}

	#heartbeat() {
		if (!this.active.peek()) return;
		if (this.estopped.peek()) {
			this.#send({ type: "estop" });
		} else {
			this.#send({ type: "drive", ...this.drive.peek() });
		}
	}

	/** Collect media timestamps at each pipeline stage for latency measurement. */
	#timestamps(): { label: string; ts: number }[] {
		const entries: { label: string; ts: number }[] = [];
		const received = this.sync.out.timestamp.peek();
		if (received != null) entries.push({ label: "received", ts: received });
		const decoded = this.videoDecoder.out.timestamp.peek();
		if (decoded != null) entries.push({ label: "decoded", ts: decoded });
		const rendered = this.videoRenderer.out.timestamp.peek();
		if (rendered != null) entries.push({ label: "rendered", ts: rendered });
		return entries;
	}

	close() {
		this.#signals.close();
	}

	// --- Effect callbacks ---

	#runActive(effect: Moq.Signals.Effect) {
		this.active.set(effect.get(this.#driving) === this.id);
	}

	#runControlLease(effect: Moq.Signals.Effect) {
		if (!effect.get(this.active)) {
			this.drive.set({ ...REST });
			this.#command.set(undefined);
			return;
		}

		this.drive.set({ ...REST });
		this.#heartbeat();
	}

	#runStatus(effect: Moq.Signals.Effect) {
		const active = effect.get(this.broadcast.out.active);
		if (!active) return;

		const statusTrack = active.subscribe("status", { priority: 10 });
		effect.cleanup(() => statusTrack.close());

		const consumer = new Json.Snapshot.Consumer(statusTrack, {
			schema: TruckStatusSchema,
			compression: true,
		});

		effect.spawn(async () => {
			let failures = 0;
			for (;;) {
				let status: TruckStatus | undefined;
				try {
					status = await consumer.next();
					failures = 0;
				} catch (err) {
					// A broken status track - e.g. RESET_STREAM from an unrelated
					// broadcast sharing a multi-tenant relay - rejects immediately
					// and forever. Never spin on it: back off between attempts, and
					// give up after a few in a row so the read loop can't peg the
					// renderer. The effect re-subscribes if the broadcast reconnects.
					console.warn("Invalid status JSON:", err);
					if (++failures >= 5) break;
					await Promise.race([effect.cancel, delay(200)]);
					continue;
				}
				if (!status) break;
				this.estopped.set(status.estopped);
				this.#acknowledge(status);
				this.status.set(status);
			}
		});
	}

	#runControl(connection: Moq.Connection.Reload, effect: Moq.Signals.Effect) {
		const conn = effect.get(connection.established);
		if (!conn) return;

		if (!effect.get(this.active)) return;

		const driverId = crypto.randomUUID().replaceAll("-", "").slice(0, 12);
		this.driverId.set(driverId);

		const driverBroadcast = new Moq.Broadcast.Producer();
		conn.publish(Moq.Path.from(`${this.#driverPrefix}/${driverId}`), driverBroadcast);
		effect.cleanup(() => {
			driverBroadcast.close();
			this.driverId.set(undefined);
			this.roundTripMs.set(undefined);
			this.#sentAt.clear();
		});

		effect.spawn(async () => {
			for (;;) {
				const req = await Promise.race([effect.cancel, driverBroadcast.requested()]);
				if (!req) break;

				if (req.name === "control") {
					// accept() commits the track's immutable properties and returns the producer.
					const track = req.accept();
					const producer = new Json.Snapshot.Producer<Record<string, unknown>>({
						track,
						compression: true,
					});
					effect.cleanup(() => producer.finish());
					effect.run(this.#runControlTrack.bind(this, track, producer));
				}
			}
		});
	}

	#runControlTrack(
		track: Moq.Track.Producer,
		producer: Json.Snapshot.Producer<Record<string, unknown>>,
		effect: Moq.Signals.Effect,
	) {
		if (effect.get(track.closed) !== undefined) return;

		const command = effect.get(this.#command);
		if (!command) return;

		producer.update({ ...command.cmd, sequence: command.sequence, timestamps: command.timestamps });
	}

	#acknowledge(status: TruckStatus) {
		if (status.driver !== this.driverId.peek() || status.sequence == null) return;
		const sentAt = this.#sentAt.get(status.sequence);
		if (sentAt == null) return;
		this.roundTripMs.set(performance.now() - sentAt);
		for (const sequence of this.#sentAt.keys()) {
			if (sequence <= status.sequence) this.#sentAt.delete(sequence);
		}
	}
}

/** Cycle gear N → F → R → N for a UI button. */
export function nextGear(gear: Gear): Gear {
	return gear === "neutral" ? "forward" : gear === "forward" ? "reverse" : "neutral";
}

/** Gear order for the shift paddles, from reverse up through forward. */
const GEARS: readonly Gear[] = ["reverse", "neutral", "forward"];

/**
 * Step one gear in `direction` without wrapping, for a wheel's shift paddles.
 *
 * Unlike {@link nextGear}, this never rolls forward straight into reverse - a
 * paddle at the end of its range does nothing rather than slamming the
 * transmission through neutral.
 */
export function shiftGear(gear: Gear, direction: 1 | -1): Gear {
	const index = GEARS.indexOf(gear);
	if (index < 0) return gear;
	return GEARS[Math.max(0, Math.min(GEARS.length - 1, index + direction))] ?? gear;
}

function stripSuffix(value: string, suffix: string): string {
	return value.endsWith(suffix) ? value.slice(0, -suffix.length) : value;
}

/** A cancel-aware sleep for backing off in effect loops. */
function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
